import { useEffect, useRef, useState } from 'react'
import s from './LemniscateLoader.module.css'
import {
  MARK_COLOR,
  LEMNISCATE_PATH,
  LEMNISCATE_POINTS,
  LoaderDriver,
  SNAKE,
  SPIRAL_PATH,
  SPIRAL_POINTS,
  STROKE,
  VIEWBOX,
  lerpPoints,
  pointsToPath,
  type Point,
} from './lemniscateGeometry'

export type LemniscateLoaderState = 'idle' | 'loading' | 'done'

export interface LemniscateLoaderProps {
  /** 'loading' plays intro then loops; 'done' finishes the current lap and settles on the spiral. */
  state: LemniscateLoaderState
  /**
   * Play the spiral -> lemniscate intro when loading begins (default true).
   * false starts directly on the lemniscate loop — for short loading surfaces
   * that unmount on completion. Read once at mount.
   */
  intro?: boolean
  /** Rendered size in px. */
  size?: number
  /** Optional status text for screen readers; without it the mark is decorative (aria-hidden). */
  label?: string
  /** Fires when the outro lands on the settled spiral (also mid-fade under reduced motion). */
  onSettled?: () => void
  className?: string
  /**
   * Any CSS colour for the mark itself (the snake, and the static shapes under
   * reduced motion) in place of the ratified orange — a call's seed colour, for
   * one. The faint track keeps its hairline. Omitted, nothing changes.
   */
  color?: string
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const TRACK_OPACITY = '0.55'

/**
 * The Ten Four loading mark: the golden-spiral logo coils into a lemniscate,
 * a solid-orange snake laps it seamlessly while loading, and on completion the
 * snake re-bites its tail and the figure unwinds back into the spiral.
 * Server-side and pre-hydration renders are the static spiral.
 */
export function LemniscateLoader({
  state,
  intro = true,
  size = 24,
  label,
  onSettled,
  className,
  color,
}: LemniscateLoaderProps) {
  // The default keeps the presentation attribute every existing caller renders;
  // a custom colour rides in as an inline style so `var(--entity-…)` resolves.
  const markStroke = color ? undefined : MARK_COLOR
  const markStyle = color ? { stroke: color } : undefined
  const trackRef = useRef<SVGPathElement | null>(null)
  const snakeRef = useRef<SVGPathElement | null>(null)
  const driverRef = useRef<LoaderDriver | null>(null)
  const rafRef = useRef<number | null>(null)
  const scratchRef = useRef<Point[] | null>(null)
  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled
  // Synchronous initial read: a reduced-motion mount must never paint one
  // animated frame first (its driver writes would linger on the swapped-in
  // static paths). Server-side renders fall back to false.
  const [reducedMotion, setReducedMotion] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(REDUCED_MOTION_QUERY).matches,
  )
  // Without the intro the first painted frame is already the snake on the
  // lemniscate at loop phase 0. Mount-time constants: React never re-patches
  // them, so they cannot fight the driver's per-frame attribute writes.
  const loopOnly = state === 'loading' && !intro
  const initialPathRef = useRef(loopOnly ? LEMNISCATE_PATH : SPIRAL_PATH)
  const initialDashRef = useRef(loopOnly ? `${SNAKE} ${1 - SNAKE}` : undefined)

  if (driverRef.current === null) {
    // Rows that mount already-finished settle instantly; no phantom outro.
    driverRef.current = new LoaderDriver(state === 'loading' ? 'idle' : state, { intro })
  }

  useEffect(() => {
    const media = window.matchMedia(REDUCED_MOTION_QUERY)
    const update = () => setReducedMotion(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    const driver = driverRef.current
    if (driver === null) return undefined
    driver.onSettled = () => {
      onSettledRef.current?.()
    }
    if (reducedMotion) {
      // No driver, no rAF: the shape itself is the information.
      if (state === 'done') {
        const timer = window.setTimeout(() => {
          onSettledRef.current?.()
        }, 320)
        return () => window.clearTimeout(timer)
      }
      return undefined
    }
    driver.input(state, performance.now())
    const apply = () => {
      const frame = driver.frame(performance.now())
      const track = trackRef.current
      const snake = snakeRef.current
      if (track === null || snake === null) return
      const d =
        frame.e <= 0
          ? SPIRAL_PATH
          : frame.e >= 1
            ? LEMNISCATE_PATH
            : pointsToPath(
                lerpPoints(
                  SPIRAL_POINTS,
                  LEMNISCATE_POINTS,
                  frame.e,
                  (scratchRef.current ??= SPIRAL_POINTS.map((p) => [p[0], p[1]])),
                ),
              )
      track.setAttribute('d', d)
      snake.setAttribute('d', d)
      track.style.opacity = frame.dash ? TRACK_OPACITY : '0'
      if (frame.dash && frame.dash.len < 0.999) {
        snake.setAttribute(
          'stroke-dasharray',
          `${frame.dash.len.toFixed(4)} ${Math.max(0, 1 - frame.dash.len).toFixed(4)}`,
        )
        snake.setAttribute('stroke-dashoffset', (-frame.dash.w0).toFixed(4))
      } else {
        snake.removeAttribute('stroke-dasharray')
        snake.removeAttribute('stroke-dashoffset')
      }
    }
    const tick = () => {
      apply()
      rafRef.current = driver.animating ? requestAnimationFrame(tick) : null
    }
    apply()
    if (driver.animating && rafRef.current === null) {
      rafRef.current = requestAnimationFrame(tick)
    }
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [state, reducedMotion])

  const svg = (
    <svg
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      width={size}
      height={size}
      className={s.svg}
      focusable="false"
      aria-hidden={label ? undefined : true}
    >
      {reducedMotion ? (
        // Distinct keys per branch: a live reduced-motion flip must mount
        // fresh paths, not inherit the other branch's driver-written attributes.
        <>
          <path
            key="static-spiral"
            className={`${s.shape} ${s.staticShape} ${state === 'loading' ? s.staticHidden : ''}`}
            d={SPIRAL_PATH}
            pathLength={1}
            stroke={markStroke}
            style={markStyle}
            strokeWidth={STROKE}
          />
          <path
            key="static-lemniscate"
            className={`${s.shape} ${s.staticShape} ${state === 'loading' ? '' : s.staticHidden}`}
            d={LEMNISCATE_PATH}
            pathLength={1}
            stroke={markStroke}
            style={markStyle}
            strokeWidth={STROKE}
          />
        </>
      ) : (
        <>
          <path
            key="anim-track"
            ref={trackRef}
            className={`${s.shape} ${s.track}`}
            d={initialPathRef.current}
            pathLength={1}
            strokeWidth={STROKE}
          />
          <path
            key="anim-snake"
            ref={snakeRef}
            className={s.shape}
            d={initialPathRef.current}
            pathLength={1}
            stroke={markStroke}
            style={markStyle}
            strokeWidth={STROKE}
            strokeDasharray={initialDashRef.current}
          />
        </>
      )}
    </svg>
  )

  if (label) {
    return (
      <span role="status" aria-live="polite" className={`${s.root} ${className ?? ''}`}>
        {svg}
        <span className={s.srOnly}>{label}</span>
      </span>
    )
  }
  return <span className={`${s.root} ${className ?? ''}`}>{svg}</span>
}
